import axios from 'axios';
import { API_URL } from '../config';
import { tenantLoginUrl } from '../utils/tenantRouting';

const api = axios.create({
  baseURL: API_URL,
});

// Attach JWT access token to every request if present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Token refresh state — prevents concurrent refresh storms
let refreshPromise = null;

function renewSession(failedToken) {
  if (refreshPromise) return refreshPromise;
  const renew = async () => {
    const currentToken = localStorage.getItem('token');
    if (currentToken && currentToken !== failedToken) return currentToken;
    const refreshToken = localStorage.getItem('refreshToken');
    const userId = localStorage.getItem('userId');
    if (!refreshToken || !userId) {
      throw Object.assign(new Error('Please sign in again.'), { response: { status: 401 } });
    }
    const res = await axios.post(`${API_URL}/api/auth/refresh`, { userId, refreshToken });
    // Do not restore credentials after logout or a different login in another tab.
    if (localStorage.getItem('refreshToken') !== refreshToken || localStorage.getItem('userId') !== userId) {
      const newerToken = localStorage.getItem('token');
      if (newerToken) return newerToken;
      throw Object.assign(new Error('Session ended.'), { response: { status: 401 } });
    }
    const { token, refreshToken: nextRefresh } = res.data;
    if (!token || !nextRefresh) throw new Error('Invalid session refresh response. Please retry.');
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', nextRefresh);
    return token;
  };
  refreshPromise = (navigator.locks
    ? navigator.locks.request('cip-session-refresh', renew)
    : renew()).finally(() => { refreshPromise = null; });
  return refreshPromise;
}

// Handle 401 globally — attempt silent refresh before redirecting to login
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    // Only attempt one refresh per failed request; skip refresh endpoint itself
    if (
      error.response?.status === 401 &&
      original && !original._retried &&
      !['/api/auth/refresh', '/api/auth/login', '/api/auth/register', '/api/auth/rsmin-login']
        .some((path) => original.url?.includes(path))
    ) {
      original._retried = true;
      try {
        const sentToken = original.headers?.Authorization?.replace(/^Bearer /, '');
        const newToken = await renewSession(sentToken);
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (refreshErr) {
        if ([400, 401, 403].includes(refreshErr.response?.status)) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userId');
          const publicAuthPage = /^\/(?:login|register|auth\/rsmin-callback)\/?$/.test(window.location.pathname)
            || /^\/t\/[^/]+\/(?:login|join)\/?$/.test(window.location.pathname);
          if (!publicAuthPage) {
            window.location.href = tenantLoginUrl(window.location.pathname, window.location.search);
          }
        }
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
